// src/components/roads/RoadWorksAiHelper.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — Road Works AI helper (tab of the Road Works report)
//   • explainable road score + findings ranked by severity, each with an action
//   • ML segment evaluation (PCI / remaining life / treatment) with rules fallback
//   • Ask FlowSense AI (Ollama, module "road_works") with a rules fallback
//   • "How this report works" guide
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Flex, HStack, VStack, Text, Badge, Button, Icon, Input, IconButton, Tooltip, Spinner, Wrap, WrapItem,
    SimpleGrid, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, useColorModeValue,
} from '@chakra-ui/react';
import { MdAutoAwesome, MdSend, MdInsights, MdArrowForward } from 'react-icons/md';
import { askFlowSenseAi } from '../../services/flowsenseAiService';
import { answerRoadLocally, buildRoadAiContext, ROAD_QUESTIONS } from './roadAiIntel';

const LEVEL_COLOR = { critical: 'red', high: 'orange', medium: 'yellow', info: 'blue' };
// --- UPDATE: action codes from the ML road model (road-pci-1.1)
const ACTION_LABEL = { routine_maintenance: 'Routine maintenance / fog seal', preventive_crack_seal: 'Crack seal & micro-surfacing', mill_and_overlay: 'Mill & 50 mm overlay', reconstruction: 'Structural reconstruction', surface_treatment: 'Surface treatment' };
// --- END UPDATE

const GUIDE = [
    { q: 'Sliced segments', a: 'The road is cut into ~100 m stations. Each has potholes, cracking, rutting and a PCI (0–100). Below 55 needs resurfacing; below 40 reconstruction.' },
    { q: 'ML evaluation', a: 'Sends every segment to the FlowSense ML road model, which returns PCI, remaining life and the recommended treatment. If the ML service is down, FlowSense uses its rules engine and labels the result.' },
    { q: 'Asphalt logistics & fleet', a: 'Truck tickets with plant, arrival, laydown and compaction temperatures. Loads below 135 °C on arrival are rejected as cold — they cannot be compacted to spec.' },
    { q: 'Inspection checklists', a: 'Tick each item that is OK, mark defects with a note and a measurement. Submitting scores the road, records the condition and can raise a repair job automatically (never twice).' },
    { q: 'QA audit', a: 'After paving, enter smoothness (IRI) and compaction. The AI audit approves, flags for a payment penalty (10% retention) or orders rework, and updates the contractor scorecard.' },
];

export default function RoadWorksAiHelper({ analysis, road, workOrder, onAction, onEvaluate, isEvaluating, mlEvaluation }) {
    const [question, setQuestion] = useState('');
    const [history, setHistory] = useState([]);
    const [isAsking, setIsAsking] = useState(false);
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    const cardBg = useColorModeValue('gray.50', 'gray.700');
    const muted = useColorModeValue('gray.600', 'gray.400');

    const ask = useCallback(async (q) => {
        const text = String(q || '').trim();
        if (!text || isAsking) return;
        setQuestion('');
        setIsAsking(true);
        let entry;
        try {
            const r = await askFlowSenseAi(text, buildRoadAiContext(analysis, { road, workOrder }), 'road_works');
            if (!r?.answer) throw new Error('Empty answer');
            entry = { q: text, answer: r.answer, source: 'ollama', model: r.model };
        } catch (e) {
            entry = { q: text, ...answerRoadLocally(text, analysis), note: e?.message };
        }
        if (mounted.current) {
            setHistory((h) => [{ ...entry, at: Date.now() }, ...h].slice(0, 6));
            setIsAsking(false);
        }
    }, [analysis, road, workOrder, isAsking]);

    if (!analysis) return <Flex justify="center" py={8}><Spinner /></Flex>;
    const s = analysis.stats;

    return (
        <VStack align="stretch" spacing={4} role="region" aria-label="Road Works AI helper">
            <Flex gap={3} align="center" wrap="wrap">
                <Tooltip hasArrow label={analysis.deductions.length ? analysis.deductions.map((d) => `−${d.points} ${d.why}`).join(' · ') : 'Length-weighted segment PCI (or the last inspection score)'}>
                    <Badge colorScheme={analysis.grade.color} fontSize="md" px={3} py={1} borderRadius="lg" cursor="help" aria-label={`Road score ${analysis.score ?? 'none'}`}>
                        {analysis.score ?? '—'} · {analysis.grade.label}
                    </Badge>
                </Tooltip>
                <Text fontSize="sm" flex={1} minW="200px">{analysis.headline}</Text>
                <Button size="sm" colorScheme="purple" variant="outline" leftIcon={<Icon as={MdInsights} />} onClick={onEvaluate} isLoading={isEvaluating} loadingText="Evaluating…" isDisabled={!s.segments && !road?.polyline_points}>
                    Evaluate segments with ML
                </Button>
            </Flex>

            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2}>
                {[
                    ['Avg PCI', s.avgPci ?? '—'],
                    ['Potholes', s.potholes],
                    ['Open WOs', `${s.openWorkOrders}${s.criticalOpen ? ` (${s.criticalOpen} crit.)` : ''}`],
                    ['Last inspection', s.daysSinceInspection === null ? 'never' : `${s.daysSinceInspection} d ago`],
                ].map(([l, v]) => (
                    <Box key={l} bg={cardBg} p={2.5} borderRadius="lg">
                        <Text fontSize="2xs" color={muted} textTransform="uppercase" fontWeight="700">{l}</Text>
                        <Text fontSize="md" fontWeight="800">{v}</Text>
                    </Box>
                ))}
            </SimpleGrid>

            {mlEvaluation && (
                <Box bg={cardBg} p={3} borderRadius="lg">
                    <HStack mb={1} spacing={2}>
                        <Text fontSize="sm" fontWeight="800">Segment evaluation</Text>
                        <Badge colorScheme={mlEvaluation.engine === 'ml' ? 'purple' : mlEvaluation.engine === 'mixed' ? 'blue' : 'gray'}>{mlEvaluation.engine === 'ml' ? 'ML model' : mlEvaluation.engine === 'mixed' ? 'ML + rules' : 'FlowSense rules'}</Badge>
                        <Text fontSize="xs" color={muted}>{mlEvaluation.evaluated} segments · avg PCI {mlEvaluation.avg_pci ?? '—'}</Text>
                    </HStack>
                    <Wrap spacing={1}>
                        {Object.entries(mlEvaluation.actions || {}).map(([k, n]) => <WrapItem key={k}><Badge variant="subtle">{ACTION_LABEL[k] || k}: {n}</Badge></WrapItem>)}
                    </Wrap>
                    {(mlEvaluation.worst || []).slice(0, 3).map((w) => (
                        <Text key={w.segment_id} fontSize="xs" mt={1}>• {w.segment_id}: PCI {w.predicted_pci}{w.predicted_rul_years != null ? `, ~${w.predicted_rul_years} yr life` : ''} → {ACTION_LABEL[w.recommended_action] || w.recommended_action}</Text>
                    ))}
                </Box>
            )}

            <Box>
                <Text fontSize="sm" fontWeight="800" mb={2}>What needs attention</Text>
                {!analysis.findings.length ? (
                    <Text fontSize="sm" color={muted}>Nothing needs attention on this road.</Text>
                ) : (
                    <VStack align="stretch" spacing={2} as="ol" style={{ listStyle: 'none' }} aria-label="Road findings">
                        {analysis.findings.map((f) => (
                            <Flex as="li" key={f.id} bg={cardBg} p={3} borderRadius="lg" borderLeftWidth="4px" borderLeftColor={`${LEVEL_COLOR[f.level]}.400`} gap={3} align="center">
                                <Box flex={1} minW={0}>
                                    <HStack spacing={2}><Badge colorScheme={LEVEL_COLOR[f.level]} fontSize="2xs">{f.level}</Badge><Text fontSize="sm" fontWeight="700">{f.title}</Text></HStack>
                                    <Text fontSize="xs" color={muted} mt={0.5}>{f.detail}</Text>
                                </Box>
                                {f.action && onAction && (
                                    <Button size="xs" rightIcon={<Icon as={MdArrowForward} />} onClick={() => onAction(f)} flexShrink={0}>{f.actionLabel}</Button>
                                )}
                            </Flex>
                        ))}
                    </VStack>
                )}
            </Box>

            <Box>
                <HStack mb={2} spacing={2}><Icon as={MdAutoAwesome} color="purple.400" /><Text fontSize="sm" fontWeight="800">Ask FlowSense AI about this road</Text></HStack>
                <Wrap spacing={1} mb={2}>
                    {ROAD_QUESTIONS.map((q) => <WrapItem key={q}><Button size="xs" variant="outline" colorScheme="purple" onClick={() => ask(q)} isDisabled={isAsking}>{q}</Button></WrapItem>)}
                </Wrap>
                <HStack as="form" onSubmit={(e) => { e.preventDefault(); ask(question); }}>
                    <Input size="sm" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Ask about segments, potholes, asphalt, inspections…" aria-label="Ask about this road" maxLength={1000} />
                    <IconButton type="submit" size="sm" colorScheme="purple" icon={isAsking ? <Spinner size="xs" /> : <Icon as={MdSend} />} isDisabled={isAsking || !question.trim()} aria-label="Send road question" />
                </HStack>
                <VStack align="stretch" spacing={2} mt={3} aria-live="polite">
                    {history.map((h) => (
                        <Box key={h.at} bg={cardBg} p={3} borderRadius="lg">
                            <HStack justify="space-between" mb={1}>
                                <Text fontSize="xs" fontWeight="700" noOfLines={1}>{h.q}</Text>
                                <Tooltip hasArrow label={h.source === 'ollama' ? 'Answered by the Ollama model using this road report.' : `FlowSense built-in rules${h.note ? ` (${h.note})` : ''}`}>
                                    <Badge colorScheme={h.source === 'ollama' ? 'purple' : 'gray'} fontSize="2xs">{h.source === 'ollama' ? `Ollama${h.model ? ` · ${h.model}` : ''}` : 'FlowSense rules'}</Badge>
                                </Tooltip>
                            </HStack>
                            <Text fontSize="sm" whiteSpace="pre-wrap">{h.answer}</Text>
                        </Box>
                    ))}
                </VStack>
            </Box>

            <Accordion allowToggle>
                <AccordionItem border="none">
                    <AccordionButton px={0}><Box flex={1} textAlign="left" fontSize="sm" fontWeight="800">How this report works</Box><AccordionIcon /></AccordionButton>
                    <AccordionPanel px={0}>
                        <VStack align="stretch" spacing={2}>
                            {GUIDE.map((g) => <Box key={g.q}><Text fontSize="xs" fontWeight="700">{g.q}</Text><Text fontSize="xs" color={muted}>{g.a}</Text></Box>)}
                        </VStack>
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>
        </VStack>
    );
}

RoadWorksAiHelper.propTypes = {
    analysis: PropTypes.object,
    road: PropTypes.object,
    workOrder: PropTypes.object,
    onAction: PropTypes.func,
    onEvaluate: PropTypes.func,
    isEvaluating: PropTypes.bool,
    mlEvaluation: PropTypes.object,
};
